import moment from 'moment';
import {ActivityType} from '../../../../../../backend/interfaces/activity-type';
import {CarePlanConfigurationFeature} from '../../../../../../backend/enums/care-plan-configuration-feature';
import {PosologyScheduleFormType} from '../../../../../../types/schedule';
import {scheduleReadableExpression} from '../../../../../functions/schedule-readable-expression';
import {
  RowData,
  ExportCarePlanGroupDataProcessor,
} from '../../base/export-care-plan-group-data-processor';
import {translations} from '../../../../../../i18n/translations';
import {PosologySchedule} from '../../../../../../backend/enums/posology-schedule';
import {ExportCarePlanTableColumnStyle} from '../../base/export-care-plan-types';
import {safeParseJson} from '../../../../../functions/safe-parse-json';
import {abbreviateMealName, getMealsActivityTypes} from './export-care-plan-medication-utils';

export class MedicationXlsxDataProcessor extends ExportCarePlanGroupDataProcessor {
  private mealActivityTypes: ActivityType[] = [];

  override async resolveTaskHeaders(firstRowData: RowData): Promise<string[]> {
    this.mealActivityTypes = await getMealsActivityTypes(this.baseData.entity.id);

    const headers = firstRowData.entries
      // filter the ones that will be custom made.
      .filter((e) => e.feature !== CarePlanConfigurationFeature.Schedule)
      // use given name
      .map((e) => e.name);

    // Schedule will be split into multiple columns. These are defined here:
    const mealsHeaders = this.mealActivityTypes.map((m) => abbreviateMealName(m.name));
    const othersHeader = this.t(translations.export.others);
    const endDateHeader = this.t(
      translations.activity_type_posology_schedule_form.fields.end_date.label
    );
    const endTimeHeader = this.t(
      translations.activity_type_posology_schedule_form.fields.end_hour.label
    );

    return [
      // Original headers
      ...headers.splice(0, headers.length - 2),
      // Schedule custom headers
      ...mealsHeaders,
      othersHeader,
      endDateHeader,
      endTimeHeader,
      // Rest of original headers
      ...headers.slice(-2),
    ];
  }

  override resolveTaskRow(rowData: RowData) {
    const rest: string[] = [];
    const periodsOfTheDay: string[] = [];
    let endDate: string = '';
    let endTime: string = '';

    for (const entry of rowData.entries) {
      switch (entry.feature) {
        // schedule will be split into multiple columns.
        case CarePlanConfigurationFeature.Schedule: {
          const schedule: PosologyScheduleFormType | undefined = safeParseJson(entry.value);

          // one column per meal activity type (periods of the day)
          for (const mealActivityType of this.mealActivityTypes) {
            if (schedule?.posologyScheduleType !== PosologySchedule.PeriodsOfTheDay) {
              periodsOfTheDay.push('');
            } else {
              const period = schedule.periodsOfTheDay?.find(
                (p) => p.activityTypeId === mealActivityType.id
              );
              periodsOfTheDay.push(period ? String(period.quantity) : '');
            }
          }

          // 'others' column: schedule readable expression (non-PeriodsOfTheDay types only)
          if (schedule?.posologyScheduleType === PosologySchedule.PeriodsOfTheDay) {
            periodsOfTheDay.push('');
          } else {
            const dateExpression = schedule
              ? scheduleReadableExpression({
                  t: this.t,
                  scheduleDefinition: schedule,
                })
              : undefined;
            periodsOfTheDay.push(dateExpression ?? this.t(translations.export.no_repetition));
          }

          // end date and time columns
          endDate = schedule?.endDate ? moment(schedule.endDate).format('DD/MM/YYYY') : '';
          endTime = schedule?.endHour ? moment(schedule.endHour).format('HH:mm') : '';
          break;
        }

        default:
          rest.push(this.resolveEntryCell(entry));
          break;
      }
    }

    return [
      // Original columns
      ...rest.slice(0, rest.length - 2),
      // Schedule custom columns
      ...periodsOfTheDay,
      endDate,
      endTime,
      // Rest of original columns
      ...rest.slice(-2),
    ];
  }

  resolveTasksColumnStyles(taskHeaders: string[]): ExportCarePlanTableColumnStyle | undefined {
    // console.log('headers', taskHeaders);
    // 0: "Ativa"
    // 1: "Hora de início"
    // 2: "Registo obrigatório"
    // 3: "Nome da tarefa"
    // 4: "Observações"
    // 5: "Data de início"
    // 6: "Medicamento"
    // 7: "Dosagem"
    // 8: "Unidade"
    // 9: "Via"
    // 10: "Box"
    // 11: "Je"
    // 12: "PA"
    // 13: "LM"
    // 14: "Al"
    // 15: "LT"
    // 16: "Ja"
    // 17: "Ce"
    // 18: "Outros"
    // 19: "Data Fim"
    // 20: "Hora Fim"
    // 21: "Turnos"
    // 22: "Planear"
    const styles: ExportCarePlanTableColumnStyle = {};

    for (let i = 0; i < taskHeaders.length; i++) {
      const header = taskHeaders[i];
      if (this.mealActivityTypes.some((m) => abbreviateMealName(m.name) === header)) {
        styles[i] = {cellWidth: 5};
      } else {
        styles[i] = {cellWidth: 20};
      }
    }

    return styles;
  }
}
